import React, { useContext, useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { OrderDirection, PageImagePositionFilterResponse } from '../../Models/Models';
import { SiteContext } from '../../Contexts';
import { ImageService } from '../../Services/ImageService';
import { Utils } from '../../Services/Utils';
import { EntityColors } from '../../Themes/EntityColors';
import CustomTooltip from '../Tooltips/CustomTooltip';
import EditButton from '../Buttons/EditButton';
import TableShell from '../Table/TableShell';
import TableToolbar from '../Table/TableToolbar';

const PageImageTableComponent = () => {
    useEffect(() => {
        Utils.setDocumentTitle('Obrázky stránok');
    }, []);

    const { filterPageImages } = ImageService();
    const [response, setResponse] = useState<PageImagePositionFilterResponse | null>(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(6);
    const [orderDirection, setOrderDirection] = useState<OrderDirection>('Desc');
    const { currentSite }: any = useContext(SiteContext);
    const { action, icon } = EntityColors.image;
    const { pageId } = useParams<{ pageId: string }>();

    const loadPage = async (pageNum: number, opts?: { perPage?: number; order?: OrderDirection }) => {
        const limit = opts?.perPage ?? perPage;
        const resp = await filterPageImages({
            websiteId: currentSite.id,
            limit,
            pageId: pageId ? Number(pageId) : undefined,
            offset: (pageNum - 1) * limit,
            orderDirection: opts?.order ?? orderDirection,
        });
        setResponse(resp ?? null);
    };

    const changePerPage = (count: number) => {
        setPerPage(count);
        setPage(1);
        void loadPage(1, { perPage: count });
    };

    const changeOrder = (dir: OrderDirection) => {
        setOrderDirection(dir);
        setPage(1);
        void loadPage(1, { order: dir });
    };

    useEffect(() => {
        setPage(1);
        void loadPage(1);
    }, [currentSite, pageId]);

    return (
        <TableShell
            title="Obrázky stránok"
            icon={icon}
            accentColor={action}
            backHref="/page/table"
            isLoading={response === null}
            isEmpty={response !== null && response.data.length === 0}
            emptyMessage="Žiadne obrázky"
            page={page}
            totalCount={response?.totalCount}
            perPage={perPage}
            onPageChange={p => { setPage(p); loadPage(p); }}
            toolbar={
                <TableToolbar
                    totalCount={response?.totalCount ?? 0}
                    nounForms={['obrázok', 'obrázky', 'obrázkov']}
                    perPage={perPage}
                    onPerPageChange={changePerPage}
                    orderDirection={orderDirection}
                    onOrderChange={changeOrder}
                />
            }
        >
            <Table responsive>
                <thead>
                    <tr>
                        <th>Náhľad</th>
                        <th>Kód</th>
                        <th>Rozmer</th>
                        <th>Popis</th>
                        <th className="w-1 text-center">Akcie</th>
                    </tr>
                </thead>
                <tbody>
                    {response?.data.map(position => (
                        <tr key={position.id}>
                            <td>
                                <img
                                    src={position.imageUsage.filePath}
                                    alt={position.imageUsage.alt}
                                    className="st-page-image-thumb"
                                />
                            </td>
                            <td><b>{position.code}</b></td>
                            <td className="text-secondary">{position.width} × {position.height}</td>
                            <td className="text-secondary">{position.imageUsage.alt}</td>
                            <td className="text-center">
                                <CustomTooltip placement="right" message="Upraviť">
                                    <Link to={`/image/select/${position.id}?websiteId=${currentSite.id}`}>
                                        <EditButton color={action} iconOnly />
                                    </Link>
                                </CustomTooltip>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </TableShell>
    );
};

export default PageImageTableComponent;
